import { BrandCard } from '@seek/branding-assets';
import { UnsafeJobHtmlRenderer } from '@seek/job-html-renderer';
import { useTranslations } from '@vocab/react';
import { Box, FieldLabel, Inline, Stack, Text } from 'braid-design-system';
import React, {
  forwardRef,
  useRef,
  type Ref,
  type RefObject,
  useContext,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SECTION_WRITE_ABOUT_YOUR_JOB } from 'src/app/shared/constants/pageSectionName';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import { Branding } from 'src/app/v1/views/components/Branding';
import { BrandingAlert } from 'src/app/v1/views/components/BrandingAlert';
import { JobSummary } from 'src/app/v1/views/components/JobSummary';
import { KeySellingPoints } from 'src/app/v1/views/components/KeySellingPoints';
import { NotProvidedText } from 'src/app/v1/views/components/NotProvidedText';
import { StandardWritingFlow } from 'src/app/v1/views/components/StandardWritingFlow';
import { Video } from 'src/app/v1/views/components/Video';
import { VideoPreview } from 'src/app/v1/views/components/Video/components/VideoPreview';
import { EditFlowDataContext } from 'src/app/v1/views/context/cajaFlowData';
import { KeySellingPointsReadOnlyView } from 'src/app/v1/views/pages/Edit/components/KeySellingPointsReadOnlyView';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/v1/views/shared/ContentLoader';
import type { AdProduct } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type {
  VideoRef,
  JobSummaryRef,
  KeySellingPointsRef,
} from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';

type ReadProps = {
  isKeySellingPointsEnabled?: boolean;
  isBrandingEnabled: boolean;
  isEditDataLoading: boolean;
};

const Read = ({
  isKeySellingPointsEnabled,
  isBrandingEnabled,
  isEditDataLoading,
}: ReadProps) => {
  const { jobDescription, jobSummary, brand, isConfidential, videoUrl } =
    useSelector(
      (state: AppState) => ({
        jobDescription: state.formData.jobDescription,
        jobSummary: state.formData.jobSummary,
        brand: state.formData.brand,
        isConfidential: state.formData.isConfidential,
        videoUrl: state.formData.videoUrl,
      }),
      shallowEqual,
    );

  const { t } = useTranslations(translations);

  const { loading } = useContext(EditFlowDataContext);

  return (
    <Stack space="large">
      <Stack space="medium">
        <Text weight="strong">
          {t('write-about-your-job-section-job-description-label')}
        </Text>
        <ContentLoader loading={loading} lines={['85%', '85%', '50%', '85%']}>
          <UnsafeJobHtmlRenderer>{jobDescription}</UnsafeJobHtmlRenderer>
        </ContentLoader>
      </Stack>
      <Stack space="medium">
        <Text weight="strong">
          {t('write-about-your-job-section-read-job-summary-label')}
        </Text>
        <ContentLoader loading={loading} lines={['85%', '85%', '50%']}>
          <Text>{jobSummary}</Text>
        </ContentLoader>
      </Stack>
      {isKeySellingPointsEnabled && <KeySellingPointsReadOnlyView />}
      {isBrandingEnabled &&
        !isEditDataLoading &&
        (isConfidential ? (
          <BrandingAlert />
        ) : (
          <Box style={{ width: '50%' }}>
            <ContentLoader loading={loading} lines={[281]} height={261}>
              <BrandCard brand={brand} showBrandId={false} />
            </ContentLoader>
          </Box>
        ))}

      <Stack space="medium">
        <Inline space="xsmall" alignY="center">
          <Text weight="strong">
            {t('write-about-your-job-section-read-video-label')}
          </Text>
          <Text tone="secondary">
            {t('write-about-your-job-section-read-video-secondary-label')}
          </Text>
        </Inline>
        <ContentLoader loading={loading} height={120} lines={[160]}>
          {videoUrl.length > 0 ? (
            <VideoPreview
              videoUrl={videoUrl}
              handleReset={() => {}}
              hideRemoveVideoButton
            />
          ) : (
            <NotProvidedText />
          )}
        </ContentLoader>
      </Stack>
    </Stack>
  );
};

interface EditProps {
  refs: {
    jobDescriptionRef: RefObject<any>;
    jobSummaryRef: RefObject<JobSummaryRef>;
    keySellingPointsRef: RefObject<KeySellingPointsRef>;
    videoRef: RefObject<VideoRef>;
  };
  isKeySellingPointsEnabled: boolean;
  isBrandingEnabled: boolean;
  customFeatures?: AdProduct['features'] | undefined;
  isEditDataLoading: boolean;
}

const Edit = ({
  refs,
  isKeySellingPointsEnabled,
  customFeatures,
  isBrandingEnabled,
  isEditDataLoading,
}: EditProps) => {
  const { t } = useTranslations(translations);
  const { isConfidential } = useSelector(
    (state: AppState) => ({
      isConfidential: state.formData.isConfidential,
    }),
    shallowEqual,
  );
  return (
    <Stack space="medium">
      <FieldLabel
        htmlFor="JobSummaryTextarea"
        label={t('write-about-your-job-section-job-description-label')}
      />
      <StandardWritingFlow ref={refs.jobDescriptionRef} />
      <FieldLabel
        htmlFor="JobSummaryTextarea"
        label={t('write-about-your-job-section-read-job-summary-label')}
      />
      <JobSummary ref={refs.jobSummaryRef} />
      {isKeySellingPointsEnabled && (
        <KeySellingPoints ref={refs.keySellingPointsRef} />
      )}
      {isBrandingEnabled &&
        !isEditDataLoading &&
        (isConfidential ? (
          <BrandingAlert />
        ) : (
          <>
            <FieldLabel
              htmlFor="keySellingPoints"
              label={t('write-about-your-job-section-branding-label')}
            />
            <Branding customFeatures={customFeatures} />
          </>
        ))}
      <Video ref={refs.videoRef} />
    </Stack>
  );
};

export const WriteAboutYourJobSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const jobDescriptionRef = useRef<any>();
    const jobSummaryRef = useRef<JobSummaryRef>(null);
    const keySellingPointsRef = useRef<KeySellingPointsRef>(null);
    const videoRef = useRef<VideoRef>(null);

    const { productFeaturesFromRedux } = useSelector(
      (state: AppState) => ({
        productFeaturesFromRedux: state.session.productFeatures,
      }),
      shallowEqual,
    );
    const productBranding = getProductFeatureByType({
      featureType: 'Branding',
      productFeatures: productFeaturesFromRedux,
    });
    const isBrandingEnabled = Boolean(productBranding);

    const doKeySellingPointsExist = getProductFeatureByType({
      featureType: 'BulletPoints',
      productFeatures: productFeaturesFromRedux,
    });
    const isKeySellingPointsEnabled = Boolean(doKeySellingPointsExist);

    const { loading: isEditDataLoading } = useContext(EditFlowDataContext);

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('write-about-your-job-section-title')}
        name={SECTION_WRITE_ABOUT_YOUR_JOB}
        ref={ref}
        fields={[
          jobDescriptionRef,
          jobSummaryRef,
          keySellingPointsRef,
          videoRef,
        ]}
        read={
          <Read
            isKeySellingPointsEnabled={isKeySellingPointsEnabled}
            isBrandingEnabled={isBrandingEnabled}
            isEditDataLoading={isEditDataLoading}
          />
        }
        edit={
          <Edit
            refs={{
              jobDescriptionRef,
              jobSummaryRef,
              keySellingPointsRef,
              videoRef,
            }}
            isKeySellingPointsEnabled={isKeySellingPointsEnabled ?? false}
            isBrandingEnabled={isBrandingEnabled}
            isEditDataLoading={isEditDataLoading}
          />
        }
      />
    );
  },
);
